/**
 * @typedef {Object} Holiday
 * @property {string} name - The name of the holiday
 * @property {Date} date - The date of the holiday
 */

/**
 * Calculate the nth occurrence of a weekday in a month
 * @param {number} year - The year
 * @param {number} monthIndex - The month index (0-based)
 * @param {number} weekday - The weekday (0=Sunday, 1=Monday, etc.)
 * @param {number} occurrence - The occurrence number (1st, 2nd, 3rd, etc.)
 * @returns {Date} The calculated date
 */
const nthWeekdayOfMonth = (year, monthIndex, weekday, occurrence) => {
  const firstOfMonth = new Date(year, monthIndex, 1);
  const firstWeekday = (weekday - firstOfMonth.getDay() + 7) % 7;
  const day = 1 + firstWeekday + (occurrence - 1) * 7;
  return new Date(year, monthIndex, day);
};

/**
 * Calculate the last occurrence of a weekday in a month
 * @param {number} year - The year
 * @param {number} monthIndex - The month index (0-based)
 * @param {number} weekday - The weekday (0=Sunday, 1=Monday, etc.)
 * @returns {Date} The calculated date
 */
const lastWeekdayOfMonth = (year, monthIndex, weekday) => {
  const firstOfNextMonth = new Date(year, monthIndex + 1, 1);
  const lastDayOfMonth = new Date(firstOfNextMonth.getTime() - 1);
  const diff = (lastDayOfMonth.getDay() - weekday + 7) % 7;
  return new Date(year, monthIndex, lastDayOfMonth.getDate() - diff);
};

/**
 * Calculate Easter Sunday using the Anonymous Gregorian algorithm (Meeus/Jones/Butcher)
 * @param {number} year - The year
 * @returns {Date} Easter Sunday date
 */
const calculateEasterSunday = (year) => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31) - 1; // 0-based month
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month, day);
};

/**
 * Create a holiday generator for a fixed date
 * @param {string} name - Holiday name
 * @param {number} monthIndex - Month index (0-based)
 * @param {number} day - Day of month
 * @returns {Function} Holiday generator function
 */
const fixedDateHoliday = (name, monthIndex, day) => {
  return (year) => ({
    name,
    date: new Date(year, monthIndex, day),
  });
};

/**
 * Create a holiday generator for the nth weekday of a month
 * @param {string} name - Holiday name
 * @param {number} monthIndex - Month index (0-based)
 * @param {number} weekday - Weekday (0=Sunday, etc.)
 * @param {number} occurrence - Which occurrence (1st, 2nd, etc.)
 * @returns {Function} Holiday generator function
 */
const nthWeekdayHoliday = (name, monthIndex, weekday, occurrence) => {
  return (year) => ({
    name,
    date: nthWeekdayOfMonth(year, monthIndex, weekday, occurrence),
  });
};

/**
 * Create a holiday generator for the last weekday of a month
 * @param {string} name - Holiday name
 * @param {number} monthIndex - Month index (0-based)
 * @param {number} weekday - Weekday (0=Sunday, etc.)
 * @returns {Function} Holiday generator function
 */
const lastWeekdayHoliday = (name, monthIndex, weekday) => {
  return (year) => ({
    name,
    date: lastWeekdayOfMonth(year, monthIndex, weekday),
  });
};

// List of all holidays with their generators
const HOLIDAY_GENERATORS = [
  fixedDateHoliday("New Year's Day", 0, 1),
  nthWeekdayHoliday("Martin Luther King Jr. Day", 0, 1, 3), // Third Monday of January
  fixedDateHoliday("Valentine's Day", 1, 14),
  nthWeekdayHoliday("Presidents' Day", 1, 1, 3), // Third Monday of February
  fixedDateHoliday("St. Patrick's Day", 2, 17),
  (year) => ({ name: 'Easter Sunday', date: calculateEasterSunday(year) }),
  nthWeekdayHoliday("Mother's Day", 4, 0, 2), // Second Sunday of May
  lastWeekdayHoliday('Memorial Day', 4, 1), // Last Monday of May
  nthWeekdayHoliday("Father's Day", 5, 0, 3), // Third Sunday of June
  fixedDateHoliday('Independence Day', 6, 4),
  nthWeekdayHoliday('Labor Day', 8, 1, 1), // First Monday of September
  fixedDateHoliday('Halloween', 9, 31),
  fixedDateHoliday("Veterans Day", 10, 11),
  nthWeekdayHoliday('Thanksgiving', 10, 4, 4), // Fourth Thursday of November
  fixedDateHoliday('Christmas Day', 11, 25),
  fixedDateHoliday("New Year's Eve", 11, 31),
];

/**
 * Get all holidays within a date range
 * @param {Date} rangeStart - Start date of range
 * @param {Date} rangeEnd - End date of range
 * @returns {Holiday[]} Array of holidays within the range
 */
export const getHolidaysInRange = (rangeStart, rangeEnd) => {
  const startYear = rangeStart.getFullYear();
  const endYear = rangeEnd.getFullYear();

  const holidays = [];

  for (let year = startYear - 1; year <= endYear + 1; year++) {
    for (const generator of HOLIDAY_GENERATORS) {
      const holiday = generator(year);
      if (holiday.date >= rangeStart && holiday.date <= rangeEnd) {
        holidays.push(holiday);
      }
    }
  }

  return holidays.sort((a, b) => a.date.getTime() - b.date.getTime());
};

/**
 * Format a holiday date as a string
 * @param {Date} date - The date to format
 * @returns {string} Formatted date string
 */
export const formatHolidayDate = (date) => {
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};
